import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueStateReasonType, IssueStateType} from '@github-ui/issue-pr-state/constants'
import {ChevronDownIcon, GearIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label, Link, Spinner, Stack} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import type {SimilarIssueUpdate} from './hooks/use-similar-issues'
import {SimilarIssueItem} from './SimilarIssueItem'
import styles from './SimilarIssues.module.css'
import {SimilarIssuesFeedback} from './SimilarIssuesFeedback'
import {
  DEFAULT_SCORE_DISPLAY_MODE,
  SCORE_DISPLAY_MODE_LABELS,
  SCORE_DISPLAY_MODES,
  type ScoreDisplayMode,
} from './utils/similarity-score'

export type SimilarIssue = {
  id: string
  /** GraphQL node ID, used for subscriptions. */
  nodeId: string
  number: number
  title: string
  state: IssueStateType
  stateReason: IssueStateReasonType
  score?: number | null
}

type SimilarIssuesProps = {
  items: SimilarIssue[]
  isLoading: boolean
  isError: boolean
  isExpanded: boolean
  onExpandedChange?: (expanded: boolean) => void
  repoName: string
  repoOwner: string
  /** When provided, clicking an issue will call this handler to open the side panel.
   * Cmd/Ctrl+click still opens the issue in a new tab. */
  onIssueClick?: (issue: SimilarIssue) => void
  /** Called when an issue is updated via subscription */
  onIssueUpdate?: (update: SimilarIssueUpdate) => void
  /** Session ID for analytics and feedback. */
  sessionId: string | undefined
  /** Called when an issue is clicked for analytics tracking. Receives position in list. */
  onIssueClickTracking?: (position: number) => void
  /** Counter to reset components on new session. */
  sessionResetKey: number
}

export function SimilarIssues({
  items,
  isLoading,
  isError,
  isExpanded,
  onExpandedChange,
  repoOwner,
  repoName,
  onIssueClick,
  onIssueUpdate,
  sessionId,
  onIssueClickTracking,
  sessionResetKey,
}: SimilarIssuesProps) {
  const [scoreDisplayMode, setScoreDisplayMode] = useState<ScoreDisplayMode>(DEFAULT_SCORE_DISPLAY_MODE)

  const isScoreEnabled = isFeatureEnabled('semantic_similarity_duplicate_issue_detection_score')
  const hasScores = isScoreEnabled && items.some(item => item.score != null)

  const handleToggle = useCallback(() => {
    onExpandedChange?.(!isExpanded)
  }, [isExpanded, onExpandedChange])

  return (
    <div className={styles.wrapper}>
      <section className={styles.container} aria-labelledby="similar-issues-heading">
        <Stack direction="horizontal" align="center" justify="space-between">
          <Stack direction="horizontal" align="center" gap="condensed">
            <IconButton
              aria-label={isExpanded ? 'Collapse similar issues' : 'Expand similar issues'}
              aria-expanded={isExpanded}
              aria-controls="similar-issues-content"
              icon={ChevronDownIcon}
              variant="invisible"
              size="small"
              onClick={handleToggle}
              className={clsx(styles.chevronButton, isExpanded && styles.chevronExpanded)}
            />
            <h3 id="similar-issues-heading" className={styles.heading}>
              Similar issues
              <span className={clsx(styles.count, !isExpanded && styles.countHidden)}>({items.length})</span>
            </h3>
            {isExpanded && isLoading && <Spinner size="small" srText="Searching for similar issues" />}
          </Stack>
          <Stack direction="horizontal" align="center" gap="condensed">
            <Label size="small" variant="secondary">
              Experimental
            </Label>
            <span aria-hidden="true">·</span>
            <Link
              aria-label="Give feedback (opens new tab)"
              href="https://gh.io/duplicate-issue-feedback"
              target="_blank"
              className={styles.feedbackLink}
            >
              Give feedback
            </Link>
            {isExpanded && hasScores && (
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton
                    aria-label="Score display settings"
                    icon={GearIcon}
                    variant="invisible"
                    size="small"
                    className={styles.settingsButton}
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay width="auto">
                  <ActionList selectionVariant="single">
                    <ActionList.GroupHeading>Score display</ActionList.GroupHeading>
                    {SCORE_DISPLAY_MODES.map(mode => (
                      <ActionList.Item
                        key={mode}
                        selected={scoreDisplayMode === mode}
                        onSelect={() => setScoreDisplayMode(mode)}
                      >
                        {SCORE_DISPLAY_MODE_LABELS[mode]}
                      </ActionList.Item>
                    ))}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )}
          </Stack>
        </Stack>
        {isExpanded && (
          <div id="similar-issues-content" className={styles.content}>
            {isError && (
              <InlineMessage className={styles.emptyState} size="small" variant="unavailable">
                Unable to load similar issues.
              </InlineMessage>
            )}
            {!isError && items.length === 0 && <p className={styles.emptyState}>No similar issues found.</p>}
            {items.length > 0 && (
              <ul className={styles.list}>
                {items.map((item, index) => (
                  <SimilarIssueItem
                    key={item.id}
                    issue={item}
                    repoOwner={repoOwner}
                    repoName={repoName}
                    onIssueClick={onIssueClick}
                    onIssueUpdate={onIssueUpdate}
                    onIssueClickTracking={onIssueClickTracking ? () => onIssueClickTracking(index + 1) : undefined}
                    scoreDisplayMode={hasScores ? scoreDisplayMode : undefined}
                  />
                ))}
              </ul>
            )}
            <SimilarIssuesFeedback
              key={sessionResetKey}
              items={items}
              repoOwner={repoOwner}
              repoName={repoName}
              sessionId={sessionId}
            />
          </div>
        )}
      </section>
    </div>
  )
}
